/**
 * The design system's easing curves, for things drawn on canvas
 * (docs/07-DESIGN-SYSTEM.md §7.2).
 *
 * CSS gets these from `tokens.css` and needs nothing here. The canvas cannot: a shape
 * morphing on the board is painted frame by frame in JavaScript, and if it eased on a
 * different curve from everything else on screen it would read as a different piece of
 * software. Same numbers, same curve, both sides of the glass.
 */

/** `--ease-out: cubic-bezier(0.16, 1, 0.3, 1)` — the default for anything entering. */
const EASE_OUT_X1 = 0.16;
const EASE_OUT_Y1 = 1;
const EASE_OUT_X2 = 0.3;
const EASE_OUT_Y2 = 1;

/** One axis of a cubic Bézier with both anchors fixed at 0 and 1. */
function bezier(t: number, a: number, b: number): number {
  const inverse = 1 - t;
  return 3 * inverse * inverse * t * a + 3 * inverse * t * t * b + t * t * t;
}

/**
 * The eased share of the way through, from the plain share of the way through.
 *
 * A CSS timing curve is written as x against y, so the time given has to be solved
 * for first. Twelve halvings put it within a four-thousandth, which is far finer than
 * a screen can show in the 150 ms this is used for.
 */
export function easeOut(progress: number): number {
  if (progress <= 0) return 0;
  if (progress >= 1) return 1;

  let low = 0;
  let high = 1;
  let t = progress;
  for (let step = 0; step < 12; step += 1) {
    const x = bezier(t, EASE_OUT_X1, EASE_OUT_X2);
    if (x < progress) low = t;
    else high = t;
    t = (low + high) / 2;
  }
  return bezier(t, EASE_OUT_Y1, EASE_OUT_Y2);
}
